export interface IndicatorDef {
  label: string;
  name: string;
  longName: string;
  /** 'advanced' = Pro/Business only. Also list new advanced indicators in functions/lib/shareGate.ts. */
  category: 'general' | 'advanced';
  /** 'candle_pane' = overlay on main pane, 'vol_pane' = reuse vol pane, undefined = new sub-pane */
  paneId?: string;
  /** Initial pane height in px for new sub-panes (defaults to 100 if omitted) */
  paneHeight?: number;
  defaultParams?: number[];
  /** 'multi': each calcParam is an independent period → one output line per param; user can add/remove lines.
   *  'fixed': params have distinct semantic roles; output lines are named but fixed in count. */
  lineMode: 'multi' | 'fixed';
  /** Output line labels — for multi: matches defaultParams order; for fixed: names the fixed output lines */
  lineLabels: string[];
  /** Human-readable labels for each calcParam (fixed-mode only) */
  paramLabels?: string[];
  /** Short plain-text description of what this indicator does */
  description: string;
  /** LaTeX string for the primary formula (display mode) */
  formulaLatex: string;
  /** Plain-text variable definitions */
  variableDefinitions: string;
  /** Event-label scan rules surfaced in the indicator's config modal */
  scanRules?: Array<{
    id:        string;
    label:     string;
    paramDefs: Array<{ key: string; label: string; default: number }>;
    color:     string;
  }>;
  /** When true, ensureBreadthData() is called before creating the indicator */
  requiresBreadthData?: boolean;
  hiddenFromBar?: boolean;   // exclude from IndicatorBar pill loops (uses a dedicated button)
  hiddenFromLegend?: boolean; // exclude from ChartLegend floating rows
  showSettingsGear?: boolean; // ChartLegend gear shows by default (params or colours are always editable); set false to opt out (e.g. CROSS_VOL, which is configured entirely from VOL's own modal)
}

// ── Default colour palettes per indicator ────────────────────────────────────
// One hex per lineLabel entry; using KLineCharts v9 default theme colours where known.
export const INDICATOR_DEFAULT_COLORS: Record<string, string[]> = {
  MA:   ['#FF9600', '#9D65C9', '#2196F3', '#E11D74'],
  EMA:  ['#FF9600', '#9D65C9', '#2196F3', '#E11D74'],
  SMA:  ['#FF9600', '#9D65C9'],
  BOLL: ['#2196F3', '#F5A623', '#E11D74'],
  BBI:  ['#FF9600', '#9D65C9', '#2196F3', '#E11D74'],
  SAR:  ['#22C55E'],
  VOL:  ['#26A69A', '#EF5350'],
  MACD: ['#2196F3', '#F5A623', '#26A69A'],
  RSI:  ['#FF9600'],
  KDJ:  ['#2196F3', '#F5A623', '#E11D74'],
  OBV:  ['#2196F3'],
  CCI:  ['#2196F3'],
  ATR:  ['#2196F3'],
  WR:   ['#FF9600', '#9D65C9', '#2196F3'],
  BIAS: ['#FF9600', '#9D65C9', '#2196F3'],
  BRAR: ['#2196F3', '#F5A623'],
  DMI:  ['#2196F3', '#F5A623', '#E11D74', '#22C55E'],
  CR:   ['#2196F3', '#F5A623', '#E11D74', '#22C55E', '#9D65C9'],
  PSY:  ['#2196F3', '#F5A623'],
  DMA:  ['#2196F3', '#F5A623', '#E11D74'],
  TRIX: ['#2196F3', '#F5A623'],
  VR:   ['#2196F3', '#F5A623'],
  MTM:  ['#2196F3', '#F5A623'],
  AO:   ['#26A69A', '#EF5350'],
  ROC:  ['#2196F3', '#F5A623'],
  PVT:  ['#2196F3'],
  MA_BREADTH:       ['#FF9600', '#9D65C9', '#2196F3', '#E11D74'],
  WEEK52_HLN:       ['#26A69A', '#EF5350', '#2196F3'],
  VOL_SPIKE_STRIP:  ['#60A5FA'],
  VOL_SPIKE_TIMELINE: ['#60A5FA'],
  MOMENTUM_OVERLAY: [],  // colors are hardcoded in the indicator draw() — not user-configurable
  MOMENTUM_TRACKER: [],
  CROSS_VOL:        ['#F59E0B'],
  PER_LINES:        ['#3B82F6', '#8B5CF6', '#EC4899'],  // Blue, Purple, Pink
  CUP_HANDLE:       [],  // colors are hardcoded in the indicator draw() — not user-configurable
  WILLIAM_ONEIL_SELL: [],  // colors are hardcoded per-rule in the indicator draw() — not user-configurable
  FOREIGN_HOLDING:  ['#22C55E', '#3B82F6', '#EC4899'],  // actual bar/MA/divergence colors are user-configurable via the config modal (chartStore fields), not this palette
};

export const INDICATORS: IndicatorDef[] = [
  // ── Overlays (candle pane) ───────────────────────────────────────────────
  {
    label: 'MA', name: 'MA', longName: 'Moving Average',
    category: 'general', paneId: 'candle_pane',
    defaultParams: [10, 20, 50, 200],
    lineMode: 'multi', lineLabels: ['MA1', 'MA2', 'MA3', 'MA4'],
    description:
      'The Moving Average smooths price data by computing the arithmetic mean over a rolling window. It helps identify the direction of the prevailing trend and dynamic support/resistance levels.',
    formulaLatex: 'MA_n(t) = \\dfrac{1}{n}\\sum_{i=0}^{n-1} C_{t-i}',
    variableDefinitions: 'C_t = closing price at bar t\nn = period length',
  },
  {
    label: 'EMA', name: 'EMA', longName: 'Exponential Moving Average',
    category: 'general', paneId: 'candle_pane',
    defaultParams: [10, 20, 50, 200],
    lineMode: 'multi', lineLabels: ['EMA1', 'EMA2', 'EMA3', 'EMA4'],
    description:
      'The Exponential Moving Average gives more weight to recent prices, making it quicker to react to new information than a simple MA. It is widely used to identify trend direction and crossover signals.',
    formulaLatex: 'EMA_n(t) = C_t \\cdot k + EMA_n(t{-}1) \\cdot (1 - k),\\quad k = \\dfrac{2}{n+1}',
    variableDefinitions: 'C_t = closing price at bar t\nn = period length\nk = smoothing factor\nEMA_n(t-1) = previous EMA value',
  },
  {
    label: 'SMA', name: 'SMA', longName: 'Simple Moving Average',
    category: 'general', paneId: 'candle_pane',
    defaultParams: [12, 2],
    lineMode: 'multi', lineLabels: ['SMA1', 'SMA2'],
    description:
      'Simple Moving Average is an unweighted arithmetic mean of the previous n closing prices. Offered as a separate indicator so two independent period lines can coexist on the chart.',
    formulaLatex: 'SMA_n(t) = \\dfrac{\\sum_{i=0}^{n-1} C_{t-i}}{n}',
    variableDefinitions: 'C_t = closing price at bar t\nn = period length',
  },
  {
    label: 'BOLL', name: 'BOLL', longName: 'Bollinger Bands',
    category: 'general', paneId: 'candle_pane',
    defaultParams: [20, 2],
    lineMode: 'fixed', lineLabels: ['Upper', 'Middle', 'Lower'],
    paramLabels: ['Period', 'Std Dev Multiplier'],
    description:
      'Bollinger Bands place upper and lower bands a set number of standard deviations above and below a moving average. When price touches the outer bands it may signal overbought/oversold conditions or a volatility breakout.',
    formulaLatex: '\\text{Mid} = MA_n,\\; \\sigma = \\text{StdDev}(C,n)\\\\ \\text{Upper} = \\text{Mid} + k\\sigma,\\; \\text{Lower} = \\text{Mid} - k\\sigma',
    variableDefinitions: 'n = period (default 20)\nk = std dev multiplier (default 2)\n\u03c3 = rolling standard deviation of close\nMid = n-period SMA',
  },
  {
    label: 'BBI', name: 'BBI', longName: 'Bull & Bear Index',
    category: 'general', paneId: 'candle_pane',
    defaultParams: [3, 6, 12, 24],
    lineMode: 'multi', lineLabels: ['BBI1', 'BBI2', 'BBI3', 'BBI4'],
    description:
      'The Bull & Bear Index averages four MAs of different periods into a composite line. Price above BBI signals a bull market; price below signals a bear market.',
    formulaLatex: 'BBI = \\dfrac{MA_3 + MA_6 + MA_{12} + MA_{24}}{4}',
    variableDefinitions: 'MA_n = n-period SMA of closing price\nDefault periods: 3, 6, 12, 24',
  },
  {
    label: 'SAR', name: 'SAR', longName: 'Parabolic SAR',
    category: 'general', paneId: 'candle_pane',
    lineMode: 'fixed', lineLabels: ['SAR'],
    description:
      'Parabolic SAR (Stop and Reverse) places dots above or below price to indicate potential reversal points. When the dots flip side it signals a trend change — commonly used for trailing stops.',
    formulaLatex: 'SAR_{t+1} = SAR_t + AF \\cdot (EP - SAR_t)',
    variableDefinitions: 'AF = acceleration factor (starts 0.02, max 0.20)\nEP = extreme point (highest high / lowest low)\nSAR_t = current SAR value',
  },
  // ── Volume pane ─────────────────────────────────────────────────────────
  {
    label: 'VOL', name: 'VOL', longName: 'Volume',
    category: 'general', paneId: 'vol_pane',
    lineMode: 'fixed', lineLabels: ['Up', 'Down'],
    description:
      'Volume displays the total number of shares traded in each bar. Rising volume confirms price moves; divergence between price and volume often precedes reversals.',
    formulaLatex: 'V_t = \\sum \\text{shares traded in bar}\\; t',
    variableDefinitions: 'V_t = volume at bar t\nGreen bars: close \u2265 open; Red bars: close < open',
    scanRules: [{
      id:    'vol_spike',
      label: 'Volume Above MA',
      paramDefs: [
        { key: 'period',    label: 'MA Period',    default: 20 },
        { key: 'threshold', label: 'Min % Above',  default: 0 },
      ],
      color: '#FF9600',
    }],
  },  // Hidden companion — always created in vol_pane; toggled via VOL modal
  {
    label: 'XVOL', name: 'CROSS_VOL', longName: 'Crossing Volume',
    category: 'general', paneId: 'vol_pane',
    lineMode: 'fixed', lineLabels: ['XVOL'],
    description: 'Institutional / HNW crossing volume overlay drawn as amber bars inside the volume pane.',
    formulaLatex: 'XV_t = \\text{cross{\\textunderscore}volume}_t',
    variableDefinitions: 'XV_t = crossing volume at bar t',
    hiddenFromBar: true,
    showSettingsGear: false,
  },  // ── Sub-pane indicators ──────────────────────────────────────────────────
  {
    label: 'MACD', name: 'MACD', longName: 'MACD',
    category: 'general',
    defaultParams: [12, 26, 9],
    lineMode: 'fixed', lineLabels: ['MACD', 'Signal', 'Histogram'],
    paramLabels: ['Fast Period', 'Slow Period', 'Signal Period'],
    description:
      'MACD measures momentum by subtracting a slow EMA from a fast EMA, then plots a signal line EMA over the result. The histogram shows the gap between MACD and Signal — a popular measure of trend momentum and reversals.',
    formulaLatex: '\\text{MACD} = EMA_{12} - EMA_{26}\\\\ \\text{Signal} = EMA_9(\\text{MACD})\\\\ \\text{Hist} = \\text{MACD} - \\text{Signal}',
    variableDefinitions: 'EMA_12 = 12-period EMA of close\nEMA_26 = 26-period EMA of close\nSignal = 9-period EMA of MACD\nHistogram = MACD \u2212 Signal',
  },
  {
    label: 'RSI', name: 'RSI', longName: 'Relative Strength Index',
    category: 'general',
    defaultParams: [14],
    lineMode: 'multi', lineLabels: ['RSI1'],
    description:
      'RSI measures the speed and magnitude of recent price changes to evaluate overbought (>70) or oversold (<30) conditions. Multiple RSI periods overlay for confirmation across timeframes.',
    formulaLatex: 'RSI_n = 100 - \\dfrac{100}{1 + RS},\\quad RS = \\dfrac{\\overline{\\text{Gain}}_n}{\\overline{\\text{Loss}}_n}',
    variableDefinitions: 'n = period length\nRS = ratio of average gain to average loss over n bars\nGain = average of up-day closes\nLoss = average of down-day closes (absolute)',
  },  // RSI_ZONES / RSI_DIVERGENCE (indicators/rsiZones.ts, rsiDivergence.ts) are
      // internal companions stacked onto RSI's pane — deliberately NOT registered
      // here so they never appear as separate catalog entries; they're only
      // reachable as toggles inside RSI's own settings modal (IndicatorConfigModal).
  {
    label: 'KDJ', name: 'KDJ', longName: 'KDJ',
    category: 'general',
    defaultParams: [9, 3, 3],
    lineMode: 'fixed', lineLabels: ['K', 'D', 'J'],
    paramLabels: ['RSV Period', 'K Period', 'D Period'],
    description:
      'KDJ derives K from a smoothed stochastic, D from a smoothed K, and J as a leading 3K\u22122D. Values above 80 indicate overbought; below 20 indicate oversold.',
    formulaLatex: 'RSV = \\dfrac{C - L_9}{H_9 - L_9}\\times100\\\\ K = \\tfrac{2}{3}K_{\\text{prev}} + \\tfrac{1}{3}RSV\\\\ D = \\tfrac{2}{3}D_{\\text{prev}} + \\tfrac{1}{3}K\\\\ J = 3K - 2D',
    variableDefinitions: 'C = close, L_9 = lowest low of 9 bars, H_9 = highest high of 9 bars\nK = smoothed stochastic, D = smoothed K\nJ = leading line (3K \u2212 2D)',
  },
  {
    label: 'OBV', name: 'OBV', longName: 'On Balance Volume',
    category: 'general',
    lineMode: 'fixed', lineLabels: ['OBV'],
    description:
      'OBV accumulates volume positively on up days and negatively on down days. Rising OBV while price is flat signals accumulation; falling OBV signals distribution.',
    formulaLatex: 'OBV_t = OBV_{t-1} + \\begin{cases} V_t & C_t > C_{t-1} \\\\ 0 & C_t = C_{t-1} \\\\ -V_t & C_t < C_{t-1} \\end{cases}',
    variableDefinitions: 'OBV_{t-1} = previous OBV\nV_t = volume at bar t\nC_t = close, C_{t-1} = previous close',
  },
  {
    label: 'CCI', name: 'CCI', longName: 'Commodity Channel Index',
    category: 'general',
    defaultParams: [20],
    lineMode: 'fixed', lineLabels: ['CCI'],
    paramLabels: ['Period'],
    description:
      'CCI measures the deviation of the typical price from its moving average relative to mean absolute deviation. Values above +100 or below \u2212100 signal strong trends.',
    formulaLatex: 'CCI = \\dfrac{\\bar{P} - MA_n(\\bar{P})}{0.015 \\cdot MAD}',
    variableDefinitions: '\u0050\u0305 = (H+L+C)/3 (typical price)\nMA_n(\u0050\u0305) = n-period SMA of typical price\nMAD = mean absolute deviation\n0.015 = Lambert\'s constant',
  },
  {
    label: 'ATR', name: 'ATR', longName: 'Average True Range',
    category: 'general',
    defaultParams: [14],
    lineMode: 'fixed', lineLabels: ['ATR'],
    paramLabels: ['Period'],
    description:
      'ATR measures market volatility by averaging the True Range over n bars. A higher ATR indicates higher volatility. It does not indicate direction \u2014 it is used to set stop distances and position sizes.',
    formulaLatex: 'TR = \\max(H{-}L,\\;|H{-}C_{\\text{prev}}|,\\;|L{-}C_{\\text{prev}}|)\\\\ ATR_n = EMA_n(TR)',
    variableDefinitions: 'H = high, L = low, C = close\nC_prev = previous bar\'s close\nTR = True Range\nn = period (default 14)',
  },
  {
    label: 'WR', name: 'WR', longName: "Williams %R",
    category: 'general',
    defaultParams: [6, 10, 14],
    lineMode: 'multi', lineLabels: ['WR1', 'WR2', 'WR3'],
    description:
      "Williams %R is a momentum oscillator ranging from \u2212100 to 0. Readings above \u221220 indicate overbought conditions; below \u221280 indicate oversold. It is the inverse of the Fast Stochastic.",
    formulaLatex: 'WR_n = \\dfrac{H_n - C}{H_n - L_n} \\times (-100)',
    variableDefinitions: 'H_n = highest high over n bars\nL_n = lowest low over n bars\nC = current close\nn = period (defaults: 6, 10, 14)',
  },
  {
    label: 'BIAS', name: 'BIAS', longName: 'Bias Ratio',
    category: 'general',
    defaultParams: [6, 12, 24],
    lineMode: 'multi', lineLabels: ['BIAS1', 'BIAS2', 'BIAS3'],
    description:
      'BIAS (Deviation Rate) measures how far price is from its moving average as a percentage. Extreme positive or negative readings suggest mean-reversion opportunities.',
    formulaLatex: 'BIAS_n = \\dfrac{C - MA_n}{MA_n} \\times 100\\%',
    variableDefinitions: 'C = current close\nMA_n = n-period SMA of close\nn = period (defaults: 6, 12, 24)',
  },
  {
    label: 'BRAR', name: 'BRAR', longName: 'BR & AR',
    category: 'general',
    defaultParams: [26],
    lineMode: 'fixed', lineLabels: ['BR', 'AR'],
    paramLabels: ['Period'],
    description:
      "BR measures market willingness to buy vs. sell relative to the previous close. AR measures buying/selling pressure using daily high\u2013open vs. open\u2013low ratios.",
    formulaLatex: 'AR = \\dfrac{\\sum(H-O)}{\\sum(O-L)}\\times100\\\\ BR = \\dfrac{\\sum(H-C_{\\text{prev}})^+}{\\sum(C_{\\text{prev}}-L)^+}\\times100',
    variableDefinitions: 'H = high, L = low, O = open, C_prev = previous close\n(x)^+ = max(x,0)\nPeriod = rolling window (default 26)',
  },
  {
    label: 'DMI', name: 'DMI', longName: 'Directional Movement Index',
    category: 'general',
    defaultParams: [14, 6],
    lineMode: 'fixed', lineLabels: ['+DI', '-DI', 'ADX', 'ADXR'],
    paramLabels: ['DI Period', 'ADX Period'],
    description:
      'DMI identifies the strength and direction of a trend using +DI (upward), \u2212DI (downward), and ADX (trend strength 0\u2013100). ADX above 25 signals a strong trend regardless of direction.',
    formulaLatex: '+DI = \\dfrac{EMA_{n}(+DM)}{ATR_{n}}\\times100\\\\ ADX = EMA_m\\!\\left(\\dfrac{|+DI - (-DI)|}{+DI+(-DI)}\\times100\\right)',
    variableDefinitions: '+DM = upward directional movement\n\u2212DM = downward directional movement\nATR_n = average true range\nADX = smoothed directional index (trend strength)',
  },
  {
    label: 'CR', name: 'CR', longName: 'CR',
    category: 'general',
    defaultParams: [26, 10, 20, 40, 60],
    lineMode: 'fixed', lineLabels: ['CR', 'MA1', 'MA2', 'MA3', 'MA4'],
    paramLabels: ['CR Period', 'MA1 Period', 'MA2 Period', 'MA3 Period', 'MA4 Period'],
    description:
      'CR (Capacity Ratio) measures buying vs. selling pressure relative to the mid-price of the previous bar. Multiple MA lines smooth the raw CR and provide trend signals.',
    formulaLatex: 'CR = \\dfrac{\\sum(H_i - M_{i-1})^+}{\\sum(M_{i-1} - L_i)^+}\\times100',
    variableDefinitions: 'H_i = high, L_i = low of bar i\nM_{i-1} = mid-price of previous bar\n(x)^+ = max(x,0)\nn = CR period (default 26)',
  },
  {
    label: 'PSY', name: 'PSY', longName: 'Psychological Line',
    category: 'general',
    defaultParams: [12, 6],
    lineMode: 'fixed', lineLabels: ['PSY', 'PSYMA'],
    paramLabels: ['Period', 'Signal Period'],
    description:
      'PSY counts the percentage of bars in the period that closed higher than the previous bar. Values above 75 suggest excessive optimism; below 25 suggest excessive pessimism.',
    formulaLatex: 'PSY_n = \\dfrac{\\text{Up-bars in period } n}{n} \\times 100',
    variableDefinitions: 'n = period (default 12)\nUp-bar = bar where C_t > C_{t-1}\nPSYMA = signal SMA of PSY',
  },
  {
    label: 'DMA', name: 'DMA', longName: 'Diff of Moving Average',
    category: 'general',
    defaultParams: [10, 50, 10],
    lineMode: 'fixed', lineLabels: ['DMA', 'AMA'],
    paramLabels: ['Fast Period', 'Slow Period', 'Signal Period'],
    description:
      'DMA plots the difference between a fast and slow MA, plus a signal MA of that difference. Crossovers of DMA and AMA generate buy/sell signals.',
    formulaLatex: 'DMA = MA_{\\text{fast}} - MA_{\\text{slow}}\\\\ AMA = MA_{m}(DMA)',
    variableDefinitions: 'MA_fast = fast-period SMA of close\nMA_slow = slow-period SMA of close\nAMA = m-period SMA of DMA (signal)',
  },
  {
    label: 'TRIX', name: 'TRIX', longName: 'Triple EMA Oscillator',
    category: 'general',
    defaultParams: [12, 9],
    lineMode: 'fixed', lineLabels: ['TRIX', 'Matrix'],
    paramLabels: ['Period', 'Signal Period'],
    description:
      'TRIX shows the rate of change of a triple-smoothed EMA, filtering out market noise. Buy/sell signals occur when TRIX crosses its signal line (Matrix).',
    formulaLatex: 'TR_1=EMA_n(C),\\; TR_2=EMA_n(TR_1),\\; TR_3=EMA_n(TR_2)\\\\ TRIX = \\dfrac{TR_3 - TR_3^{\\text{prev}}}{TR_3^{\\text{prev}}} \\times 100',
    variableDefinitions: 'C = closing price, n = period (default 12)\nTR_1, TR_2, TR_3 = successive EMA passes\nMatrix = m-period EMA of TRIX (default 9)',
  },
  {
    label: 'VR', name: 'VR', longName: 'Volume Ratio',
    category: 'general',
    defaultParams: [26, 6],
    lineMode: 'fixed', lineLabels: ['VR', 'MAVR'],
    paramLabels: ['Period', 'Signal Period'],
    description:
      'Volume Ratio compares the sum of volumes on up-days to down-days. Readings above 350 suggest overbought; below 40 suggest oversold. MAVR is a signal SMA over VR.',
    formulaLatex: 'VR = \\dfrac{\\sum V_{\\uparrow} + 0.5\\sum V_{-}}{\\sum V_{\\downarrow} + 0.5\\sum V_{-}} \\times 100',
    variableDefinitions: 'V\u2191 = volume on up bars (close > prev close)\nV\u2193 = volume on down bars\nV\u2212 = volume on flat bars',
  },
  {
    label: 'MTM', name: 'MTM', longName: 'Momentum',
    category: 'general',
    defaultParams: [12, 6],
    lineMode: 'fixed', lineLabels: ['MTM', 'MAMTM'],
    paramLabels: ['Period', 'Signal Period'],
    description:
      'Momentum measures the rate of price change by subtracting the closing price n bars ago from the current close. MAMTM smooths the value. Positive = upward momentum; negative = downward.',
    formulaLatex: 'MTM_n(t) = C_t - C_{t-n}',
    variableDefinitions: 'C_t = current close\nC_{t-n} = close n bars ago\nn = period (default 12)\nMAMTM = signal SMA of MTM',
  },
  {
    label: 'AO', name: 'AO', longName: 'Awesome Oscillator',
    category: 'general',
    lineMode: 'fixed', lineLabels: ['AO Up', 'AO Down'],
    description:
      'The Awesome Oscillator (Bill Williams) is the difference between a 5-bar and 34-bar SMA of the median price. Histogram bars are green when the value is rising, red when falling.',
    formulaLatex: 'AO = SMA_5\\!\\left(\\tfrac{H+L}{2}\\right) - SMA_{34}\\!\\left(\\tfrac{H+L}{2}\\right)',
    variableDefinitions: 'H = high, L = low\nMedian = (H+L)/2\nSMA_5 = 5-period SMA of median\nSMA_34 = 34-period SMA of median',
  },
  {
    label: 'ROC', name: 'ROC', longName: 'Rate of Change',
    category: 'general',
    defaultParams: [12, 6],
    lineMode: 'fixed', lineLabels: ['ROC', 'MAROC'],
    paramLabels: ['Period', 'Signal Period'],
    description:
      'Rate of Change measures the percentage change in price over n bars, showing whether momentum is increasing or decreasing. MAROC is a moving average of ROC.',
    formulaLatex: 'ROC_n(t) = \\dfrac{C_t - C_{t-n}}{C_{t-n}} \\times 100',
    variableDefinitions: 'C_t = current close\nC_{t-n} = close n bars ago\nn = period (default 12)\nMAROC = signal SMA of ROC',
  },
  {
    label: 'PVT', name: 'PVT', longName: 'Price Volume Trend',
    category: 'general',
    lineMode: 'fixed', lineLabels: ['PVT'],
    description:
      'PVT is a cumulative indicator combining price percentage change with volume. Similar to OBV but weights volume by the proportional price change rather than a binary up/down signal.',
    formulaLatex: 'PVT_t = PVT_{t-1} + V_t \\cdot \\dfrac{C_t - C_{t-1}}{C_{t-1}}',
    variableDefinitions: 'PVT_{t-1} = previous PVT value\nV_t = volume at bar t\nC_t = close, C_{t-1} = previous close',
  },

  // ── Advanced: market breadth indicators ──────────────────────────────────
  {
    label: 'MA Breadth', name: 'MA_BREADTH', longName: 'Stocks Above Moving Average',
    category: 'advanced', requiresBreadthData: true,
    lineMode: 'fixed',
    lineLabels: ['SMA10', 'EMA21', 'EMA50', 'EMA200'],
    description:
      'Number of CSE stocks trading above each moving average on each day. A rising line indicates improving breadth; a falling line indicates deteriorating breadth.',
    formulaLatex: '\\sum_{i=1}^{N} \\mathbf{1}[C_i > MA_i]',
    variableDefinitions: 'N = total active stocks\nC_i = split-adjusted close\nMA_i = moving average for stock i',
  },
  {
    label: '52W H/L', name: 'WEEK52_HLN', longName: '52-Week Highs/Lows + Net',
    category: 'advanced', requiresBreadthData: true,
    lineMode: 'fixed',
    lineLabels: ['New Highs', 'New Lows', 'Net'],
    description:
      'Green bars show 52-week new highs; red bars show new lows below zero. The blue net line (highs − lows) overlays both — a thrust above zero marks breadth expansion.',
    formulaLatex: 'H = \\#\\{C_i = \\max_{252}(C_i)\\},\\quad L = \\#\\{C_i = \\min_{252}(C_i)\\},\\quad Net = H - L',
    variableDefinitions: 'C_i = split-adjusted close\nmax_{252} / min_{252} = highest / lowest close over past 252 trading days',
  },

  // ── Advanced: volume spike visualisations ────────────────────────────────
  {
    label: 'Spike Strip', name: 'VOL_SPIKE_STRIP', longName: 'Volume Spike Intensity Strip',
    category: 'advanced',
    paneHeight: 48,
    defaultParams: [20],
    lineMode: 'fixed', lineLabels: ['Label Color'],    paramLabels: ['MA Period'],
    description:
      'Every trading day becomes a thin colour-coded cell that spans the full pane height. '      + 'The heat-map colour encodes how far above the n-period volume MA that day\u2019s volume was: '
      + 'grey-blue = at or below the MA, amber \u2192 orange \u2192 deep red = progressively elevated. '
      + 'This view is immune to overflow at any zoom level, works for hundreds of bars, and lets '
      + 'you instantly spot clusters of elevated volume\u2014for example a distribution phase or '
      + 'an accumulation swing. Hover any cell to see the exact % deviation in the legend.',
    formulaLatex: '\\text{Dev}_t = \\dfrac{V_t - \\text{MA}_n(V)}{\\text{MA}_n(V)} \\times 100',
    variableDefinitions:
      'V_t = volume at bar t\n'
      + 'n = MA period (default 20)\n'
      + 'MA_n(V) = n-period simple moving average of volume\n'
      + 'Dev_t < 0 → below MA (grey)\n'
      + 'Dev_t = 50 % → amber\n'
      + 'Dev_t = 150 % → orange\n'
      + 'Dev_t ≥ 300 % → deep red',
  },
  {
    label: 'Spike TL', name: 'VOL_SPIKE_TIMELINE', longName: 'Volume Spike Timeline',
    category: 'advanced',
    paneHeight: 140,
    defaultParams: [20],
    lineMode: 'fixed', lineLabels: ['Label Color'],
    paramLabels: ['MA Period'],
    description:
      'Only days where volume exceeds the n-period MA are plotted\u2014as circles (\u201cbubbles\u201d). '
      + 'The y-axis shows % above the MA, so spikes from different time periods can be compared '
      + 'directly to each other on an apples-to-apples basis. Bubble radius grows with severity '
      + '(proportional to \u221a% above MA), and colour deepens through the same amber \u2192 orange \u2192 red '
      + 'heat ramp as the Spike Strip. Non-spike days produce no mark, so the pane is completely '
      + 'immune to overcrowding regardless of zoom level. A dashed horizontal baseline marks the '
      + '\u201cat MA\u201d level (0 %) for visual reference.',
    formulaLatex:
      '\\text{Dev}_t = \\dfrac{V_t - \\text{MA}_n(V)}{\\text{MA}_n(V)} \\times 100\\\\'
      + '\\text{shown only when } \\text{Dev}_t > 0',
    variableDefinitions:
      'V_t = volume at bar t\n'
      + 'n = MA period (default 20)\n'
      + 'MA_n(V) = n-period simple moving average of volume\n'
      + 'Bubble radius \u221d \u221aDev_t (min 4 px, max 22 px)\n'
      + 'Non-spike days (Dev_t \u2264 0) are invisible',
  },
  // ── Power Trend Momentum
  {
    label: 'Overlay', name: 'MOMENTUM_OVERLAY', longName: 'Power Trend Momentum Overlay',
    category: 'advanced',
    showSettingsGear: true,
    paneId: 'candle_pane',
    lineMode: 'fixed', lineLabels: [],
    description:
      'Power Trend overlay: green background fill while all 4 conditions are active, '
      + 'EMA21 (blue) + SMA50 (amber dashed) lines, signal vertical (green) on trigger bar, '
      + 'exit verticals (red = 10% drawdown, amber = EMA21<SMA50), and a live stop level line.',
    formulaLatex:
      'C_1: L_t \\geq EMA_{21}\\text{ for }\\geq10\\text{ bars}\\\\'
      + 'C_2: EMA_{21}>SMA_{50}\\text{ for }\\geq5\\text{ bars}\\\\'
      + 'C_3: SMA_{50,t}>SMA_{50,t-1}\\\\'
      + 'C_4: C_t>O_t\\quad\\text{(green candle)}',
    variableDefinitions:
      'EMA21 = 21-period exponential moving average (\u03b1 = 2/22)\n'
      + 'SMA50 = 50-period simple moving average\n'
      + 'C1 streak resets to 0 on any bar where low < EMA21\n'
      + 'C2 streak resets to 0 when EMA21 ≤ SMA50\n'
      + 'Stop level = peak close since signal \u00d7 0.90',
  },
  {
    label: 'Mom Tracker', name: 'MOMENTUM_TRACKER', longName: 'Power Trend Condition Tracker',
    category: 'advanced',
    hiddenFromBar: true,
    showSettingsGear: true,
    paneHeight: 80,
    lineMode: 'fixed', lineLabels: [],
    description:
      'Sub-pane bar chart showing how many of the 3 pre-conditions (C1/C2/C3) '
      + 'are satisfied on each bar. Full bright-green when the power trend is active.',
    formulaLatex: '\\text{count} = C_1 + C_2 + C_3 \\in \\{0,1,2,3\\}',
    variableDefinitions:
      'Each bar height = count / 3 of the pane height\n'
      + 'Full-height bright green = power trend active (all 4 conditions met)',
  },

  // ── RS Line (not a klinecharts indicator — uses custom overlay hook)
  {
    label: 'RS', name: 'RS_LINE', longName: 'Relative Strength Line & Rating',
    category: 'advanced',
    paneId: 'candle_pane',
    hiddenFromLegend: true,
    lineMode: 'fixed', lineLabels: [],
    description:
      'Relative Strength (RS) Line compares a stock\'s price performance to a benchmark index. '
      + 'The RS Rating is a percentile score (1-99) based on the stock\'s price change relative to '
      + 'the market over the past 12 months.',
    formulaLatex: '',
    variableDefinitions: '',
  },

  // ── Valuation Indicators ────────────────────────────────────────────────
  {
    label: 'PER Lines', name: 'PER_LINES', longName: 'TTM / Growth / Avg EPS PER',
    category: 'general',
    paneHeight: 100,
    lineMode: 'fixed', lineLabels: ['TTM PER', 'Growth PER', 'Avg EPS PER'],
    description:
      'Daily price-to-earnings ratios derived from quarterly EPS data. TTM PER uses trailing-twelve-month earnings; '
      + 'Growth PER uses forward-estimated earnings; Avg EPS PER uses average EPS estimate. '
      + 'Each line updates when a new quarter becomes available, and moves daily with closing price between quarters.',
    formulaLatex: 'TTM\\,PER = \\dfrac{P}{EPS_{T4Q}},\\quad Growth\\,PER = \\dfrac{P}{EPS_{Grow}},\\quad Avg\\,EPS\\,PER = \\dfrac{P}{EPS_{Avg}}',
    variableDefinitions: 'P = current bar close price\nEPS_{T4Q} = sum of last 4 quarters earnings per share\nEPS_{Grow} = forward growth-adjusted EPS estimate\nEPS_{Avg} = annualized average EPS from available quarters',
  },
  {
    label: 'EPS', name: 'EPS_LINE', longName: 'Raw EPS on Secondary Left Axis',
    category: 'advanced',
    showSettingsGear: true,
    paneId: 'candle_pane',
    lineMode: 'fixed', lineLabels: ['EPS'],
    description:
      'Raw quarterly EPS plotted on a dedicated secondary Y-axis on the left of the price pane. '
      + 'Supports linear and symmetric-log scale, optional announcement-date alignment, '
      + 'YoY growth labels, and a forward quarter projection using configurable models.',
    formulaLatex: '\\text{symLog}(v) = \\text{sgn}(v) \\cdot \\log_{10}(1 + |v|)',
    variableDefinitions:
      'EPS        — quarterly earnings per share\n'
      + 'symLog     — symmetric log transform (handles negatives)\n'
      + 'Projection — next quarter estimate via TTM YoY, 2Y CAGR, or quarter momentum',
  },

  // ── Foreign Holding (dedicated sub-pane) ─────────────────────────────────
  {
    label: 'FGN', name: 'FOREIGN_HOLDING', longName: 'Foreign Holding',
    category: 'advanced',
    paneHeight: 120,
    showSettingsGear: true,
    lineMode: 'fixed', lineLabels: ['Holding', 'MA 1', 'MA 2'],
    description:
      'Absolute shares held by foreign investors, one bar per report date, plus two '
      + 'configurable moving averages (10/20 snapshots by default) of that same series. '
      + 'Each bar is colored by comparison to the previous snapshot — increase, decrease, '
      + 'or unchanged — and a small marker flags snapshots where foreign-holding direction '
      + 'confirms or diverges from price direction. Ownership %, absolute count, and the '
      + 'change since the prior snapshot are shown in the crosshair legend.',
    formulaLatex:
      '\\text{Holding}_t=\\text{foreign\\_holding}_t,\\quad'
      + '\\text{MA}_n(t)=\\frac{1}{n}\\sum_{i=0}^{n-1}\\text{Holding}_{t-i}',
    variableDefinitions:
      'foreign_holding_t — shares held by foreign investors at report date t\n'
      + 'n — MA period, in snapshots (configurable, default 10 and 20)\n'
      + 'Bar color — up/down/no-change vs foreign_holding at the prior report date\n'
      + 'Marker — sign(Δ close) vs sign(Δ foreign_holding) over the same span',
  },

  // ── William O'Neil / IBD sell signals ────────────────────────────────────
  {
    label: "O'Neil Sell", name: 'WILLIAM_ONEIL_SELL', longName: "William O'Neil Sell Signals",
    category: 'advanced',
    showSettingsGear: true,
    paneId: 'candle_pane',
    lineMode: 'fixed', lineLabels: [],
    description:
      "Six independently configurable IBD/CAN SLIM-style sell rules: a break below a key "
      + '(default 50-bar) moving average on above-average volume, a break below the long-term '
      + '(default 200-bar) moving average, a climax top (largest single-bar gain of the advance '
      + 'on the heaviest volume of the advance), an exhaustion gap after an extended run, '
      + 'clustering distribution days (heavy-volume down days), and a pivot stop-loss line '
      + 'measured from the most recent confirmed breakout pivot rather than a manual cost basis. '
      + 'Each rule can be shown/hidden and re-parameterized independently in the settings modal.',
    formulaLatex:
      '\\text{Pivot Stop} = P_{\\text{pivot}} \\times (1 - \\text{stop\\%})',
    variableDefinitions:
      'P_pivot — most recent pivot high that price has since closed above (a confirmed breakout)\n'
      + 'stop% — configurable stop distance below the pivot (default 7.5%, the IBD 7-8% rule)\n'
      + 'Distribution day — a down day (default ≥0.2%) on volume above a multiple of the prior day\'s volume\n'
      + 'Climax top / exhaustion gap both require a minimum prior advance to qualify as an "extended run"',
  },

  // ── Cup & Handle pattern recognition ─────────────────────────────────────
  {
    label: 'Cup & Handle', name: 'CUP_HANDLE', longName: 'Cup & Handle Pattern',
    category: 'advanced',
    showSettingsGear: true,
    paneId: 'candle_pane',
    lineMode: 'fixed', lineLabels: [],
    description:
      'Detects the most recent IBD/MarketSmith-style cup-and-handle base: a rounded '
      + 'consolidation (the "cup") fed by a genuine prior uptrend, followed by a shallow '
      + 'pullback in the upper half of the cup (the "handle") before a breakout. Draws the '
      + 'rim, rounded cup floor, handle box, and buy-point line, plus a confidence score '
      + 'combining depth, length, U-shape roundness, rim symmetry, handle quality, and '
      + 'prior-trend strength.',
    formulaLatex:
      'Confidence = 0.20\\,S_{depth} + 0.15\\,S_{length} + 0.25\\,S_{round} + 0.15\\,S_{sym} + 0.20\\,S_{handle} + 0.05\\,S_{trend}',
    variableDefinitions:
      'S_depth  — triangular fit of cup depth against the configured band\n'
      + 'S_length — triangular fit of cup length (bars) against the configured band\n'
      + 'S_round  — fraction of closes correctly rounded above/below the cup midline\n'
      + 'S_sym    — closeness of the right rim to the left rim\n'
      + 'S_handle — handle depth/length fit + volume dry-up bonus (0 until a handle forms)\n'
      + 'S_trend  — strength of the leg-up feeding the base beyond the minimum threshold\n'
      + 'Weights redistribute across the other five when no handle has formed yet.',
  },
];


/** True for indicators that own a separate sub-pane with a ChartLegend row (excludes VOL and candle-pane overlays). */
export function isUserSubPaneIndicator(name: string): boolean {
  const def = INDICATORS.find((i) => i.name === name);
  return !!def && !def.paneId && !def.hiddenFromLegend;
}
